import React, { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { Action } from 'redux';

import { CallStatus, OrderSideEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { getCompanyLogoAndName as getLogoAndName, loadStock as serviceLoadStock } from '../../lib/store/fundamental';
import { subscribeSymbolStatus, unsubscribeSymbolStatus } from '../../lib/store/gateway';
import { PaymentCall } from '../../lib/store/payment/types';
import { getAccountTradeHistory, getOpenPositions } from '../../lib/store/reporting';
import { PENDING_ORDERS_REQUEST_TENNSTATUS, TRADE_HISTORY_ORDERS_TENNSTATUSES } from '../../lib/store/reporting/constants';
import { AccountTradeHistoryPayloadData, OpenPosition } from '../../lib/store/reporting/types';
import { getCurrentAccount, isSymbolETF as isETF, isUserVerifiedStatus } from '../../lib/store/selectors';
import { getBuyingPower } from '../../lib/store/trading';
import MarketStateCache from '../../lib/store-util/MarketStateCache';
import SymbolStatusCache from '../../lib/store-util/SymbolStatusCache';
import { checkIfSymbolIsHalted } from '../../lib/util/HaltSymbolHelper';
import { isMarketRegularHours } from '../../lib/util/MarketDataHelpers';
import { manageSubscriptions } from '../../lib/util/TradingHelpers';

import {
  CapitalBuySellButtonsConditions,
  GoBackButton,
  LatestNews,
  NotVerifiedNotification,
  OrderNavigation,
  RegularTradingHoursStartModal,
  ShortSellOrderPlace,
  SymbolDetailsHeader,
  SymbolDetailsNotActiveBox,
} from '../../components';
import NavButtons from '../../components/NavigationButtons/NavButtons';
import CreateOrderModal from '../../components/OrderActions/CreateOrder/CreateOrderModal';
import HaltedSymbol from '../../components/OrderActions/HaltedSymbol/HaltedSymbol';
import OrderResponseManager from '../../components/OrderResponseManager/OrderResponseManager';
import { useIsMobile } from '../../hooks';
import { BASE_WIDTH_SYMBOL_DETAILS } from '../../util/constants';

import ScrollWrapper from './ScrollWrapper';
import { Financials, Overview, Ratios } from './Tabs';

import styles from './SymbolDetails.module.scss';

type Props = ReduxProps & RouteComponentProps;

const SymbolDetails = (props: Props) => {
  const {
    accountReferenceId,
    openPositions,
    transactions,
    pendingOrders,
    tradeHistoryOrders,
    hasDeposit,
    companyLogoAndName,
    individualData,
    isPaymentsReady,
    statementData,
    isSymbolETF,
    loadStock,
    getOrders,
    getPositions,
    getCompanyLogoAndName,
    fetchBuyingPowerAndPutGlobalState,
    subscribeForSymbolStatus,
    unsubscribeForSymbolStatus,
  } = props;
  const isMobile = useIsMobile();
  const { name: symbol }: { name: string } = useParams();
  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [visibleHaltedSymbolDialog, setVisibleHaltedSymbolDialog] = useState<boolean>(false);
  const [visibleWarningShortSellDialog, setVisibleWarningShortSellDialog] = useState<boolean>(false);
  const [visibleRegularTradingHoursStartDialog, setVisibleRegularTradingHoursStartDialog] = useState<boolean>(false);
  const [openOrderModal, setOpenOrderModal] = useState<{target: string; isModalOpened: boolean}>(
    { target: '', isModalOpened: false },
  );

  const positionsBySymbol = openPositions.find((position: OpenPosition) => (position.symbol === symbol));
  const isUserVerified = isUserVerifiedStatus(individualData);
  const symbolsStatusCache = SymbolStatusCache.use(symbol);
  const history = useHistory();

  useEffect(() => {
    if (accountReferenceId) {
      fetchBuyingPowerAndPutGlobalState();
    }
  }, [accountReferenceId]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    MarketStateCache.refresh();
    subscribeForSymbolStatus([symbol]);
    manageSubscriptions('symbol-details', symbol);

    return () => {
      unsubscribeForSymbolStatus();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  useEffect(() => {
    loadStock(symbol);
  }, [symbol, loadStock]);

  useEffect(() => {
    if (!companyLogoAndName[symbol]) {
      getCompanyLogoAndName(symbol);
    }
  }, [companyLogoAndName, getCompanyLogoAndName, symbol]);

  useEffect(() => {
    if (!isInitialised) {
      setIsInitialised(true);
    }
  }, [isInitialised]);

  useEffect(() => {
    if (!openPositions.find(position => position.symbol === symbol)) {
      getPositions(symbol);
    }
    if (!pendingOrders.find(order => order.symbol === symbol)) {
      getOrders({ symbol, tenNStatus: PENDING_ORDERS_REQUEST_TENNSTATUS, isPendingOrdersCall: true });
    }
    if (!tradeHistoryOrders.find(order => order.symbol === symbol)) {
      getOrders({ symbol, tenNStatus: TRADE_HISTORY_ORDERS_TENNSTATUSES });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const closeRegularTradingHoursStartDialog = () => {
    setVisibleRegularTradingHoursStartDialog(false);
  };

  const reviewOrder = (target: string) => {
    isMobile && history.replace({
      pathname: `/${target.includes(OrderSideEnum.Buy) ? OrderSideEnum.Buy : OrderSideEnum.Sell}/${symbol}`,
      state: { stateSymbol: symbol, stateTarget: target, origin: history.location.pathname },
    });

    setOpenOrderModal({ target, isModalOpened: true });
  };

  const handleCreateOrderSellOrBuy = (target: string, isModalOpened: boolean) => {
    if (target === OrderSideEnum.Sell && !isMarketRegularHours() && !positionsBySymbol) {
      setVisibleRegularTradingHoursStartDialog(true);
      return;
    }

    if (checkIfSymbolIsHalted(symbolsStatusCache)) {
      setVisibleHaltedSymbolDialog(true);
      return;
    }

    if (isMobile && !positionsBySymbol && isMarketRegularHours() && target === OrderSideEnum.Sell) {
      setVisibleWarningShortSellDialog(true);
      return;
    }

    reviewOrder(target);
  };

  const proceedShortSellOrderPlaceModal = () => {
    setVisibleWarningShortSellDialog(false);
    reviewOrder(OrderSideEnum.SellShort);
  };

  const renderSellAndBuyButtons = () => (
    <div className={styles.buttonsWrapper}>
      <CapitalBuySellButtonsConditions>
        <>
          <button
            className={styles.sellBtn}
            type="button"
            onClick={() => {
              handleCreateOrderSellOrBuy(OrderSideEnum.Sell, true);
            }}
          >
            {lang.commonOrderSideButtonSell()}
          </button>
          <button
            className={styles.buyBtn}
            type="button"
            onClick={() => { handleCreateOrderSellOrBuy(OrderSideEnum.Buy, true); }}
          >
            {lang.commonOrderSideButtonBuy()}
          </button>
        </>
      </CapitalBuySellButtonsConditions>
    </div>
  );

  const getInitialActiveTab = () => (statementData.symbol === symbol ? 2 : 0);

  const tabs = [
    { text: lang.symbolDetailsTabOverview(),
      component: <Overview symbol={symbol} renderSellAndBuyButtons={renderSellAndBuyButtons} />,
    },
    { text: lang.symbolDetailsTabNews(),
      component: <LatestNews isExtended selectedSymbol={symbol} />,
    },
  ];

  if (!isSymbolETF(symbol)) {
    tabs.push({ text: lang.symbolDetailsTabFinancials(),
      component: <Financials symbol={symbol} />,
    });
  }

  tabs.push({ text: lang.symbolDetailsTabRatios(),
    component: <Ratios symbol={symbol} />,
  });

  return (
    <>
      {!isMobile && (
        <div className={styles.backButton}>
          <GoBackButton />
        </div>
      )}
      <div className={isMobile // eslint-disable-line no-nested-ternary
        ? classNames(styles.wrapperMobile, styles.noPadding)
        : styles.wrapper}
      >
        <div className={isMobile ? styles.symbolInfoWrapperMobile : styles.symbolInfoWrapper}>
          <OrderResponseManager />
          {visibleHaltedSymbolDialog && !positionsBySymbol && (
            <HaltedSymbol
              symbolName={companyLogoAndName[symbol].name}
              toggleModal={() => { setVisibleHaltedSymbolDialog(false); }}
            />
          )}
          {visibleWarningShortSellDialog && !positionsBySymbol && (
            <ShortSellOrderPlace
              processModal={proceedShortSellOrderPlaceModal}
              toggleModal={() => { setVisibleWarningShortSellDialog(false); }}
            />
          )}
          {visibleRegularTradingHoursStartDialog && !positionsBySymbol && (
            <RegularTradingHoursStartModal
              toggleModal={closeRegularTradingHoursStartDialog}
            />
          )}
          {
            (window.innerWidth <= BASE_WIDTH_SYMBOL_DETAILS
              && individualData) && !isMobile
            && (
            <NotVerifiedNotification
              individual={individualData}
              hasUserName={false}
              isPaymentsReady={isPaymentsReady}
            />
            )
          }
          <SymbolDetailsHeader
            symbol={symbol}
            handleCreateOrderSellOrBuy={handleCreateOrderSellOrBuy}
          />
          <div className={classNames(styles.navigationBar, { [styles.navigationBarMob]: isMobile })}>
            <NavButtons
              tabs={tabs}
              setActiveTab={getInitialActiveTab()}
            />
          </div>
        </div>

        {!isMobile && (
          <>
            <ScrollWrapper>
              {
                window.innerWidth > BASE_WIDTH_SYMBOL_DETAILS
                && (
                <div className={styles.wrapperNavigation}>
                  <OrderNavigation symbol={symbol} />
                </div>
                )
              }
              {
                ((!isUserVerified || isPaymentsReady) && (!hasDeposit && isEmpty(transactions) && individualData))
                && window.innerWidth > BASE_WIDTH_SYMBOL_DETAILS
                && (
                  <>
                    <SymbolDetailsNotActiveBox className={['d-none', 'd-lg-block', styles.notVerified].join(' ')} />
                  </>
                )
              }
            </ScrollWrapper>
            {!isEmpty(transactions) && openOrderModal.isModalOpened && (
              <CreateOrderModal symbol={symbol} openOrderModal={openOrderModal} />
            )}
          </>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountReferenceId: getCurrentAccount(state),
  companyLogoAndName: state.fundamental.companyLogoAndName,
  hasDeposit: state.payment.hasDeposit,
  openPositions: state.reporting.openPositions,
  pendingOrders: state.reporting.pendingOrders,
  tradeHistoryOrders: state.reporting.tradeHistoryOrders,
  individualData: state.crm.individualExtendedInfo,
  isSymbolETF: (symbol: string) => isETF(symbol, state),
  isPaymentsReady: state.payment.statusByCall[PaymentCall.getPaymentsAll]?.status === CallStatus.READY,
  statementData: state.fundamental.statementsData,
  transactions: state.payment.paymentsAll.data,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  loadStock: (symbol: string) => dispatch(serviceLoadStock({ symbol })),
  getCompanyLogoAndName: (symbol: string) => dispatch(getLogoAndName(symbol)),
  getPositions: (symbol: string) => dispatch(getOpenPositions({ symbol, group: 'symbol-details' })),
  getOrders: (data: AccountTradeHistoryPayloadData) => {
    dispatch(getAccountTradeHistory({ ...data, isSymbolDetails: true }));
  },
  fetchBuyingPowerAndPutGlobalState: () => dispatch(getBuyingPower()),
  subscribeForSymbolStatus: (symbol: string[]) => dispatch(subscribeSymbolStatus(symbol)),
  unsubscribeForSymbolStatus: () => dispatch(unsubscribeSymbolStatus()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(SymbolDetails);
